import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, User, TrendingUp, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestorsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [investorCredentials, setInvestorCredentials] = useState({ user_id: '', password: '' });

  const handleInvestorLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/investor/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(investorCredentials)
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('investorToken', data.token);
        localStorage.setItem('investorId', data.investor_id);
        localStorage.setItem('userRole', 'investor');
        navigate('/investor/dashboard');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-blue-300 text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/20">
            Investor Portal
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Welcome, Investors
          </h1>
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Access your investment dashboard, track portfolio performance, and stay updated with the latest company news.
          </p>
        </div>
      </section>

      {/* Login Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <CardTitle>Investor Login</CardTitle>
              <CardDescription>
                Enter your credentials to access your investment dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvestorLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="investor-uid">User ID</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="investor-uid"
                      placeholder="Enter your User ID"
                      className="pl-10"
                      value={investorCredentials.user_id}
                      onChange={(e) => setInvestorCredentials({...investorCredentials, user_id: e.target.value})}
                      required
                      data-testid="investor-user-id"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="investor-pwd">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="investor-pwd"
                      type="password"
                      placeholder="Enter your password"
                      className="pl-10"
                      value={investorCredentials.password}
                      onChange={(e) => setInvestorCredentials({...investorCredentials, password: e.target.value})}
                      required
                      data-testid="investor-password"
                    />
                  </div>
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600"
                  disabled={isLoading}
                  data-testid="investor-login-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    'Login to Dashboard'
                  )}
                </Button>
              </form>
              <div className="mt-6 pt-4 border-t text-center">
                <Link to="/" className="text-sm text-blue-600 hover:underline">
                  ← Back to Home
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Investor Dashboard Features</h2>
            <p className="text-gray-600">Everything you need to track your investment in STEAM-IE</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Portfolio Tracking', desc: 'Real-time view of your investment value and shares' },
              { title: 'ROI Analysis', desc: 'Track your returns and investment growth over time' },
              { title: 'Company Updates', desc: 'Stay informed with latest news and announcements' },
              { title: 'Dividend Info', desc: 'View dividend history and upcoming payments' }
            ].map((item, index) => (
              <div key={index} className="p-6 bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center mb-4 text-white font-bold">
                  {index + 1}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InvestorsPage;
